import React, { FC, useCallback, useEffect } from 'react'
import styles from 'styles/components/template.module.scss'
import text from 'styles/utility/text.module.scss'
import typography from 'styles/components/typography.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { State } from 'index'
import { Spacer } from 'components/Utility'
import { MainHeadline, SubDescription } from 'components/Typography'
import { BlueButton, NavButton, ScrollTopButton } from 'components/Button'
import { AlertCard } from 'components/Card'
import { DatePicker, PostalCodeForm, SelectBox, TextInput } from 'components/Form'
import { FormLabel } from 'components/Label'
import { stateOptions } from 'constants/options'
import { useSelect, useString } from 'hooks/form'
import classNames from 'classnames'
import { format } from 'date-fns'
import { fetchStoreDeliverySettings, saveStoreDeliverySetting } from 'reducks/storeDeliverySetting/operations'
import { getStoreRef } from 'reducks/auth/selectors'
import { getStoreDeliverySettings } from 'reducks/storeDeliverySetting/selectors'
import { StoreDeliverySettingType } from 'types/storeDeliverySetting'
import { fetchOrders, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'
import { getOrders } from 'reducks/checkout/selectors'
import { fetchStoreShippings, initStoreShippings, unsubscribeStoreShippings } from 'reducks/storeShipping/operations'
import { getStoreShippings } from 'reducks/storeShipping/selectors'
import { exportDeliveryOrders } from 'lib/order'
import { isValidPhoneNumber } from 'lib/validator'
import { StockUnionType } from 'types/stock'
import { useFetchStoreDeliveryBreads } from 'hooks/storeDeliveryBread'

const orderTypes = ['delivery'] as StockUnionType[]

const ExportOrderTemplate: FC = () => {
  const dispatch = useDispatch()
  const store = useSelector((state: State) => state.store)
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const storeDeliverySettings = useSelector((state: State) => getStoreDeliverySettings(state))
  const storeShippings = useSelector((state: State) => getStoreShippings(state))
  const orders = useSelector((state: State) => getOrders(state))
  const pathname = useSelector((state: State) => state.router.location.pathname)
  const query = useSelector((state: State) => state.router.location.query)
  const status = query.status || 'すべて'
  const storeDeliveryBreads = useFetchStoreDeliveryBreads()

  const date = /\/order\/delivery\/export\/.+/.test(pathname) ? pathname.split('/order/delivery/export/')[1] : undefined
  const strToday = format(new Date(), 'yyyy-MM-dd')

  const { state: scheduledDeliveryDate, handleState: handleScheduledDeliveryDate } = useString<string>(strToday)
  const { state: storeName, handleState: handleStoreName, setState: setStoreName } = useString<string>('')
  const { state: phoneNumber, handleState: handlePhoneNumber, setState: setPhoneNumber } = useString<string>('')
  const {
    state: firstPostalCode,
    handleState: handleFirstPostalCode,
    setState: setFirstPostalCode,
  } = useString<string>('')
  const {
    state: secondPostalCode,
    handleState: handleSecondPostalCode,
    setState: setSecondPostalCode,
  } = useString<string>('')
  const { state: state, handleState: selectState, setState: setState } = useSelect<string>('')
  const { state: city, handleState: handleCity, setState: setCity } = useString<string>('')
  const { state: street, handleState: handleStreet, setState: setStreet } = useString<string>('')
  const { state: building, handleState: handleBuilding, setState: setBuilding } = useString<string>('')
  const { state: customerCode, handleState: handleCustomerCode, setState: setCustomerCode } = useString<string>('')
  const {
    state: feeManagementNumber,
    handleState: handleFeeManagementNumber,
    setState: setFeeManagementNumber,
  } = useString<string>('')

  const goToYamatoBusinessMembersSettingPage = () => {
    dispatch(push('/delivery/setting/yamato'))
  }

  const exportCsv = useCallback(() => {
    if (!date) return
    // CSVを出力する
    exportDeliveryOrders({
      date,
      scheduledDeliveryDate,
      storeName,
      phoneNumber,
      firstPostalCode,
      secondPostalCode,
      state,
      city,
      street,
      building,
      customerCode,
      feeManagementNumber,
      orders,
      storeShippings,
      storeDeliveryBreads,
    })
    // 入力されたヤマトビジネスメンバーズ情報を保存する
    const storeDeliverySetting: StoreDeliverySettingType = {
      customer_code: customerCode,
      fee_management_number: feeManagementNumber,
    }
    dispatch(saveStoreDeliverySetting(storeRef, 'yamato', storeDeliverySetting))
  }, [
    date,
    scheduledDeliveryDate,
    storeName,
    phoneNumber,
    firstPostalCode,
    secondPostalCode,
    state,
    city,
    street,
    building,
    customerCode,
    feeManagementNumber,
    storeRef,
    orders,
    storeShippings,
    storeDeliveryBreads,
  ])

  // 店舗の設定をフィルイン
  useEffect(() => {
    if (!store) return
    setStoreName(store.name)
    setPhoneNumber(store.phone_number)
    setFirstPostalCode(store.address.postal_code.slice(0, 3))
    setSecondPostalCode(store.address.postal_code.slice(3, 7))
    setState(store.address.state)
    setCity(store.address.city)
    setStreet(store.address.street)
    setBuilding(store.address.building)
  }, [store])

  // ヤマトビジネスメンバーズ情報を取得してセット
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreDeliverySettings(storeRef))
  }, [storeRef])

  useEffect(() => {
    if (!storeDeliverySettings || storeDeliverySettings.length === 0) return
    const yamatoSetting = storeDeliverySettings.find(
      (storeDeliverySetting) => storeDeliverySetting.ref?.id === 'yamato'
    )
    if (!yamatoSetting) return
    setCustomerCode(yamatoSetting.customer_code)
    setFeeManagementNumber(yamatoSetting.fee_management_number)
  }, [storeDeliverySettings])

  // 注文情報を再取得する
  useEffect(() => {
    if (!date || !storeRef) return
    dispatch(initOrders())
    dispatch(fetchOrders(date, status, orderTypes, ['succeeded', 'requires_capture'], storeRef))
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [date, status, orderTypes, storeRef])

  // 配送先情報を取得する
  useEffect(() => {
    if (!date || !storeRef) return
    dispatch(initStoreShippings())
    dispatch(fetchStoreShippings(date, storeRef))
    return () => {
      dispatch(unsubscribeStoreShippings())
    }
  }, [date, storeRef])

  const isExportButtonDisabled =
    scheduledDeliveryDate === '' ||
    storeName === '' ||
    phoneNumber === '' ||
    !isValidPhoneNumber(phoneNumber) ||
    firstPostalCode.length !== 3 ||
    secondPostalCode.length !== 4 ||
    state === '' ||
    city === '' ||
    street === '' ||
    customerCode.length < 10 ||
    customerCode.length > 12 ||
    feeManagementNumber.length !== 2

  return (
    <div className={styles.standard}>
      <Spacer size={20} />
      <AlertCard
        headline={
          '※お客さまの方でヤマト運輸の送り状発行システムB2クラウドの登録が必要です。\nCSV出力後内容にお間違いがないかご自身で必ずご確認ください。また商品配送後は必ず管理画面から商品を発送しましたボタンを押してください。'
        }
        title={'CSV出力時の注意'}
      />
      <Spacer size={20} />
      <MainHeadline headline={'アカウント情報'} />
      <Spacer size={8} />
      <SubDescription description={'注文商品や注文したお客さま住所は自動でCSVに反映されます。'} />
      <Spacer size={20} />
      <FormLabel id={'scheduled-delivery-date'} label={'出荷予定日'} required={true} />
      <Spacer size={8} />
      <DatePicker min={strToday} onChange={handleScheduledDeliveryDate} value={scheduledDeliveryDate} />
      <Spacer size={20} />
      <TextInput
        disabled={false}
        label={'ご依頼主名'}
        onChange={handleStoreName}
        placeholder={'店名が入ります'}
        required={true}
        size={'small'}
        type={'text'}
        value={storeName}
      />
      <Spacer size={20} />
      <TextInput
        disabled={false}
        label={'ご依頼主電話番号'}
        onChange={handlePhoneNumber}
        placeholder={'01234567890'}
        required={true}
        size={'small'}
        type={'tel'}
        value={phoneNumber}
      />
      <Spacer size={20} />
      <PostalCodeForm
        label={'ご依頼主郵便番号'}
        handleFirstPostalCode={handleFirstPostalCode}
        handleSecondPostalCode={handleSecondPostalCode}
        firstPostalCode={firstPostalCode}
        secondPostalCode={secondPostalCode}
      />
      <Spacer size={20} />
      <FormLabel id={'address'} label={'ご依頼主住所'} required={true} />
      <Spacer size={8} />
      <SelectBox options={stateOptions} select={selectState} value={state} />
      <Spacer size={8} />
      <TextInput
        disabled={false}
        onChange={handleCity}
        placeholder={'市区町村'}
        required={true}
        size={'small'}
        type={'text'}
        value={city}
      />
      <Spacer size={8} />
      <TextInput
        disabled={false}
        onChange={handleStreet}
        placeholder={'住所1'}
        required={true}
        size={'small'}
        type={'text'}
        value={street}
      />
      <Spacer size={8} />
      <TextInput
        disabled={false}
        onChange={handleBuilding}
        placeholder={'ビル名'}
        required={true}
        size={'small'}
        type={'text'}
        value={building}
      />
      <Spacer size={20} />
      <FormLabel id={'customer-code'} label={'請求先顧客コード'} required={true} />
      <Spacer size={4} />
      <p className={classNames(typography.description_sub)}>
        ヤマトビジネスメンバーズにログインして、入力してください。初回登録以降自動で保存されます。
        <a
          className={classNames(text.underline, text.color_blue_main)}
          onClick={goToYamatoBusinessMembersSettingPage}
          role="button"
        >
          配送設定 &gt; ヤマトビジネスメンバーズ情報
        </a>
        から登録された情報の編集が可能です。
      </p>
      <Spacer size={8} />
      <TextInput
        disabled={false}
        maxLength={12}
        onChange={handleCustomerCode}
        placeholder={'012345678901'}
        required={true}
        size={'small'}
        type={'tel'}
        value={customerCode}
      />
      <Spacer size={20} />
      <FormLabel id={'fee-management-number'} label={'運賃管理番号'} required={true} />
      <Spacer size={4} />
      <p className={classNames(typography.description_sub)}>
        ヤマトビジネスメンバーズにログインして、入力してください。初回登録以降自動で保存されます。
        <a
          className={classNames(text.underline, text.color_blue_main)}
          onClick={goToYamatoBusinessMembersSettingPage}
          role="button"
        >
          配送設定 &gt; ヤマトビジネスメンバーズ情報
        </a>
        から登録された情報の編集が可能です。
      </p>
      <Spacer size={8} />
      <TextInput
        disabled={false}
        maxLength={2}
        onChange={handleFeeManagementNumber}
        placeholder={'01'}
        required={true}
        size={'small'}
        type={'tel'}
        value={feeManagementNumber}
      />
      <Spacer size={28} />
      <BlueButton
        disabled={isExportButtonDisabled}
        label={'送り状CSV出力'}
        onClick={exportCsv}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={28} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={72} />
      <ScrollTopButton />
      <Spacer size={20} />
    </div>
  )
}

export default ExportOrderTemplate
